import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, User } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Wordmark } from "@/components/Logo";
import { EMAIL, scrollToId } from "@/lib/api";

export const NAV = [
  { id: "home", label: "Home", note: "The throne room" },
  { id: "services", label: "Services", note: "What I craft" },
  { id: "pricing", label: "Pricing", note: "$20 / $40 / $40" },
  { id: "work", label: "Work", note: "Portfolio" },
  { id: "videos", label: "Videos", note: "In motion" },
  { id: "about", label: "About", note: "The designer" },
  { id: "contact", label: "Contact", note: "Commission me" },
];

const MenuOverlay = ({ onPick }) => (
  <motion.div
    data-testid="menu-overlay"
    className="fixed inset-0 z-40 bg-[var(--void)]/97 backdrop-blur-xl flex"
    initial={{ clipPath: "circle(0% at 95% 4%)" }}
    animate={{ clipPath: "circle(150% at 95% 4%)" }}
    exit={{ clipPath: "circle(0% at 95% 4%)" }}
    transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
  >
    <div className="hidden lg:block w-[38%] relative overflow-hidden border-r border-[var(--line)]">
      <img src="/art/vamp-throne.jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
      <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[var(--void)]" />
    </div>
    <nav className="flex-1 flex flex-col justify-center px-6 sm:px-16 pt-24 pb-10 overflow-y-auto">
      {NAV.map((n, i) => (
        <motion.button
          key={n.id}
          data-testid={`menu-link-${n.id}`}
          onClick={() => onPick(n.id)}
          className="group flex items-baseline gap-5 py-3 sm:py-4 border-b border-[var(--line)] text-left"
          initial={{ opacity: 0, x: 60 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.25 + i * 0.07, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <span className="font-mono text-xs text-[var(--blood)]">0{i + 1}</span>
          <span className="font-display font-bold text-4xl sm:text-6xl tracking-tight transition-colors duration-300 group-hover:text-[var(--gold)] group-hover:italic">
            {n.label}
          </span>
          <span className="ml-auto font-mono text-xs tracking-widest uppercase text-[var(--ink-dim)] hidden sm:block">{n.note}</span>
        </motion.button>
      ))}
      <Link to="/account" data-testid="menu-account-link" className="mt-8 font-mono text-sm tracking-widest uppercase text-[var(--ink)] hover:text-[var(--gold)] flex items-center gap-2">
        <User className="w-4 h-4" /> My account
      </Link>
      <a href={`mailto:${EMAIL}`} data-testid="menu-email-link" className="mt-4 font-mono text-sm text-[var(--gold-soft)] flex items-center gap-2">
        <Mail className="w-4 h-4" /> {EMAIL}
      </a>
    </nav>
  </motion.div>
);

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  useEffect(() => {
    if (open) window.__lenis?.stop(); else window.__lenis?.start();
  }, [open]);

  const pick = (id) => { setOpen(false); setTimeout(() => scrollToId(id), 450); };

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color] duration-500 border-b ${scrolled && !open ? "bg-[var(--void)]/80 backdrop-blur-md border-[var(--line)]" : "border-transparent"}`}
      >
        <div className="max-w-[1400px] mx-auto px-5 sm:px-10 h-[72px] flex items-center justify-between">
          <button data-testid="logo-home-btn" onClick={() => pick("home")}><Wordmark /></button>
          <div className="hidden lg:flex items-center gap-8">
            {NAV.slice(1).map((n) => (
              <button key={n.id} data-testid={`nav-${n.id}-link`} onClick={() => scrollToId(n.id)}
                className="font-mono text-xs tracking-[0.25em] uppercase text-[var(--ink-dim)] hover:text-[var(--gold)] transition-colors">
                {n.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-5">
          <Link to="/account" data-testid="header-account-link"
            className="flex items-center gap-2 font-mono text-xs tracking-[0.25em] uppercase text-[var(--ink-dim)] hover:text-[var(--gold)] transition-colors">
            <User className="w-4 h-4" /> <span className="hidden sm:inline">{user ? (user.name?.split(" ")[0] || "Account") : "Log in"}</span>
          </Link>
          <button data-testid="menu-toggle-btn" onClick={() => setOpen((o) => !o)}
            className="group flex items-center gap-3 font-mono text-xs tracking-[0.3em] uppercase">
            <span className="hidden sm:inline text-[var(--ink-dim)] group-hover:text-[var(--gold)] transition-colors">{open ? "Close" : "Menu"}</span>
            <span className="relative w-11 h-11 border border-[var(--gold)]/60 rotate-45 grid place-items-center transition-colors group-hover:bg-[var(--gold)]/10">
              <span className={`absolute w-4 h-px bg-[var(--gold)] -rotate-45 transition-transform duration-300 ${open ? "rotate-45" : "-translate-y-1"}`} />
              <span className={`absolute w-4 h-px bg-[var(--gold)] -rotate-45 transition-transform duration-300 ${open ? "-rotate-[135deg]" : "translate-y-1"}`} />
            </span>
          </button>
          </div>
        </div>
      </header>
      <AnimatePresence>{open && <MenuOverlay onPick={pick} />}</AnimatePresence>
    </>
  );
};
